import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { CET_TIME_ZONE, COMMON_MESSAGES } from 'src/constants/Common';

dayjs.extend(utc);
dayjs.extend(timezone);

export type PositionPeriod = 'day' | 'week' | 'month' | 'year';
export type PeriodResolution = 'hour' | 'date' | 'month';
export type PeriodEndpoints = Record<PeriodResolution, string>;

export type PeriodColumn = {
  key: string;
  label: string;
  start: number;
  end: number;
};

export const DEFAULT_PERIOD: PositionPeriod = 'day';

export const PERIOD_OPTIONS: { value: PositionPeriod; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Calendar Year' },
];

const PERIOD_RESOLUTION: Record<PositionPeriod, PeriodResolution> = {
  day: 'hour',
  week: 'date',
  month: 'date',
  year: 'month',
};

const GAS_DAY_START_HOUR = 6;
const HOURS_IN_DAY = 24;
const DAYS_IN_WEEK = 7;
const MONTHS_IN_YEAR = 12;
const HOUR_MS = 60 * 60 * 1000;

const toDateString = (value: Dayjs) => value.format(COMMON_MESSAGES.DATE_YYYY_MM_DD);
const padTwoDigits = (value: number) => String(value).padStart(2, '0');
const toColumnKey = (prefix: string, value: number) => `${prefix}${padTwoDigits(value)}`;
const getGasDayStart = (date: Dayjs) =>
  dayjs.tz(`${toDateString(date)} ${padTwoDigits(GAS_DAY_START_HOUR)}:00`, CET_TIME_ZONE).valueOf();

export const getPeriodEndpoint = (endpoints: PeriodEndpoints, period: PositionPeriod) =>
  endpoints[PERIOD_RESOLUTION[period]];

export const getPeriodRange = (gasDay: string, period: PositionPeriod) => {
  const date = dayjs(gasDay);
  switch (period) {
    case 'week':
      return { fromDate: gasDay, toDate: toDateString(date.add(DAYS_IN_WEEK - 1, 'day')) };
    case 'month':
      return {
        fromDate: toDateString(date.startOf('month')),
        toDate: toDateString(date.endOf('month')),
      };
    case 'year':
      return {
        fromDate: toDateString(date.startOf('year')),
        toDate: toDateString(date.endOf('year')),
      };
    default:
      return { fromDate: gasDay, toDate: gasDay };
  }
};

// Each column carries its gas-day time bounds so deal quantities can be aggregated per column.
export const getPeriodColumns = (gasDay: string, period: PositionPeriod): PeriodColumn[] => {
  const { fromDate, toDate } = getPeriodRange(gasDay, period);
  const from = dayjs(fromDate);

  if (period === 'day') {
    const gasDayStart = getGasDayStart(from);
    return Array.from({ length: HOURS_IN_DAY }, (_, index) => {
      const hour = (index + GAS_DAY_START_HOUR) % HOURS_IN_DAY;
      const start = gasDayStart + index * HOUR_MS;
      return { key: toColumnKey('h', hour), label: `${hour}:00`, start, end: start + HOUR_MS };
    });
  }

  if (period === 'year') {
    return Array.from({ length: MONTHS_IN_YEAR }, (_, index) => {
      const month = from.add(index, 'month');
      return {
        key: toColumnKey('m', index + 1),
        label: month.format('MMM YYYY'),
        start: getGasDayStart(month),
        end: getGasDayStart(month.add(1, 'month')),
      };
    });
  }

  const dayCount = dayjs(toDate).diff(from, 'day') + 1;
  return Array.from({ length: dayCount }, (_, index) => {
    const date = from.add(index, 'day');
    return {
      key: toColumnKey('d', index + 1),
      label: date.format('DD MMM YYYY'),
      start: getGasDayStart(date),
      end: getGasDayStart(date.add(1, 'day')),
    };
  });
};

// Deal quantity is hourly, so a column's value is quantity × hourly slots active within it.
export const getDealColumnValue = (
  column: PeriodColumn,
  startEpoch: number | null,
  endEpoch: number | null,
  quantity: number | null | undefined,
) => {
  if (startEpoch == null || endEpoch == null || quantity == null) {
    return 0;
  }
  const overlapStart = Math.max(column.start, startEpoch);
  const overlapEnd = Math.min(column.end, endEpoch);
  if (overlapEnd <= overlapStart) {
    return 0;
  }
  const activeHours =
    Math.ceil((overlapEnd - column.start) / HOUR_MS) -
    Math.ceil((overlapStart - column.start) / HOUR_MS);
  return quantity * activeHours;
};
